import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createVNode as _createVNode, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "--filled"
}
const _hoisted_2 = {
  key: 1,
  class: "--warning"
}

import { ElButton, ElCol, ElDialog, ElIcon, ElRow, ElTabs, ElTabPane, ElForm, ElSkeleton } from 'element-plus';
import { computed, ref, toRaw } from 'vue';
import { useStore } from 'vuex';
import { SuccessFilled, Select as IconSelect } from '@element-plus/icons-vue';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash.clonedeep';
import ClipIcon from '@/components/icons/Clip.vue';
import UploadIcon from '@/components/icons/Upload.vue';
import GeneralTable from '@/components/Form/GeneralTable.vue';
import { DocumentType, DocumentTypeEnum, MediaFile, Lang, Locale, MediaFields } from '@/types/manuals';
import { cleanEmptyFileFields, createGeneralManualsByVideo, reOrder }
  from '@/router/Admin/builderManuals';
import LangIcon from '@/components/Form/LangIcon.vue';
import { localesNamesMapper } from '@/constants/languages';
import { showValidationError, UploadedPayloadType } from '@/utils';


export default /*#__PURE__*/_defineComponent({
  __name: 'ManualsGeneral',
  props: {
    type: {}
  },
  emits: ["close", "saveGlobalFiles"],
  setup(__props: any, { emit: __emit }) {

const store = useStore();
const { t } = useI18n();

const emit = __emit;

const props = __props;

const manualsForm = ref();
const requiredLocales = computed(() => store.getters['Locales/requiredLocales']);
const supportedLocales = computed(() => store.getters['Locales/supportedLocales']);
const tabLocales = computed(() => {
  const locales = cloneDeep(store.getters['Locales/locales']);
  return locales.map((loc: any, localIndex: number) => {
    loc.isFilled = (() => {
      const isFilled: any[] = [];

      if (generalList.value.length) {
        generalList.value.forEach((item: MediaFile, itemIndex: number) => {
          if (rules.value?.[`${fieldName}.${itemIndex}.locales.${localIndex}.caption`]?.[0]?.required) {
            isFilled.push(item.locales[localIndex].caption);
          }
          if (rules.value?.[`${fieldName}.${itemIndex}.locales.${localIndex}.file`]?.[0]?.required) {
            isFilled.push(item.locales[localIndex].file);
          }
          if (rules.value?.[`${fieldName}.${itemIndex}.flag`]?.[0]?.required) {
            isFilled.push(item.flag);
          }
        });
      }

      const hasEmptyField = Boolean(isFilled.filter((field: string) => field === '').length);
      return !hasEmptyField;
    })();

    return loc;
  });
});
const activeTab = ref<Lang>('ru');
const isVideo = props.type === DocumentTypeEnum.Video;
const fieldName = isVideo ? MediaFields.VideoInstructions : MediaFields.UsersGuides;

const generalList = computed(() => (isVideo
  ? store.getters['Manuals/getGeneralVideoManuals']
  : store.getters['Manuals/getGeneralDocManuals']));

const isLoading = computed(() => store.getters['Manuals/getIsManualsLoading']);

const formData = computed(() => ({ [fieldName]: generalList.value }));

const rules = computed(() => {
  const data = {};
  generalList.value.forEach((item: MediaFile, indexItem: number) => {
    item?.locales?.forEach((locItem, locIndex) => {
      const required = requiredLocales.value.includes(locItem.locale);
      data[`${fieldName}.${indexItem}.locales.${locIndex}.file`] = [{ required, message: '', trigger: 'blur' }];
      data[`${fieldName}.${indexItem}.flag`] = [{ required: true, message: '', trigger: 'blur' }];
      data[`${fieldName}.${indexItem}.locales.${locIndex}.caption`] = [{ required, message: '', trigger: 'blur' }];
    });
  });

  return data;
});

const updateManualLink = (index: number, link: string, indexLang?: number) => {
  const loc = generalList.value[index].locales
    .find((x: Locale, i: number) => x.locale === activeTab.value || i === indexLang);
  loc.file = link;
  loc.is_file_changed = 1;
};

const changeFile = ({ data, index, indexLang }:
  { data: UploadedPayloadType, index: number, indexLang: number }) => {
  updateManualLink(index, data.name, indexLang);
};

const inputTextButton = (value: { index: number, value: string }) => {
  generalList.value[value.index].locales
    .find((x: Locale) => x.locale === activeTab.value).caption = value.value;
};
const inputFileLink = (value: { index: number, value: string }) => {
  updateManualLink(value.index, value.value);
};
const changeFlag = (value: { index: number, value: string }) => {
  generalList.value[value.index].flag = value.value;
};
const handleDelete = (value: number) => {
  generalList.value.splice(value, 1);
};

const addGeneralLink = (): void => {
  store.commit('Manuals/ADD_NEW_MANUALS', { isFile: false, isVideo });
};

const addGeneralFile = (): void => {
  store.commit('Manuals/ADD_NEW_MANUALS', { isFile: true, isVideo });
};

const undoChanges = () => {
  emit('close');
  store.commit('Manuals/RESET_MANUALS', isVideo);
};

const checkValid = async () => {
  await manualsForm.value.validate(async (valid: boolean, fields: any) => {
    if (valid) {
      await saveChanges();
    } else {
      showValidationError({
        fields,
        t,
        supportedLocales: supportedLocales.value,
        isScrollToError: false,
        showManualTitle: false,
      });
    }
  });
};

const saveChanges = async () => {
  let generalListWithoutEmptyFileFields = cleanEmptyFileFields(generalList.value);
  generalListWithoutEmptyFileFields = reOrder(generalListWithoutEmptyFileFields);
  const data = createGeneralManualsByVideo(toRaw(generalListWithoutEmptyFileFields), isVideo);

  try {
    store.commit('Common/SET_SHOW_LOADER', true);
    await store.dispatch('Manuals/saveGeneralManuals', { data, isVideo });
  } finally {
    store.commit('Common/SET_SHOW_LOADER', false);
  }

  emit('saveGlobalFiles');
  emit('close');
};

store.dispatch('Manuals/fetchGeneralManuals', isVideo);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(ElDialog), {
    title: isVideo ? _unref(t)('commonVideo') : _unref(t)('commonDocs'),
    class: "general-dialog"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(ElForm), {
        ref_key: "manualsForm",
        ref: manualsForm,
        model: formData.value,
        rules: rules.value,
        statusIcon: "",
        validateOnRuleChange: false
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(ElTabs), {
            modelValue: activeTab.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeTab).value = $event)),
            type: "border-card",
            class: "add-page__tabs add-page__gap"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabLocales.value, (lang) => {
                return (_openBlock(), _createBlock(_unref(ElTabPane), {
                  key: `tab-${lang.value}`,
                  name: lang.value
                }, {
                  label: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(localesNamesMapper)[lang.value]) + " ", 1),
                    _createVNode(_unref(ElIcon), { class: "el-icon--right" }, {
                      default: _withCtx(() => [
                        (lang.isRequired && lang.isFilled)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                              _createVNode(_unref(IconSelect))
                            ]))
                          : (lang.isRequired)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_2))
                            : _createCommentVNode("", true),
                        _createVNode(LangIcon, {
                          lang: lang.value
                        }, null, 8, ["lang"])
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_unref(ElSkeleton), {
                      animated: "",
                      loading: isLoading.value
                    }, null, 8, ["loading"]),
                    (!isLoading.value)
                      ? (_openBlock(), _createBlock(GeneralTable, {
                          key: 0,
                          list: generalList.value,
                          tabLang: lang.value,
                          type: _ctx.type,
                          onInputFileLink: inputFileLink,
                          onInputTextButton: inputTextButton,
                          onChangeFlag: changeFlag,
                          onChangeFile: changeFile,
                          onDelete: handleDelete
                        }, null, 8, ["list", "tabLang", "type"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1032, ["name"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(_unref(ElRow), null, {
            default: _withCtx(() => [
              _createVNode(_unref(ElCol), {
                offset: 3,
                class: "files-group__action-footer"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ElButton), {
                    round: "",
                    onClick: addGeneralLink
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('buttons.addByLink')) + " ", 1),
                      _createVNode(_unref(ElIcon), { class: "el-icon--right" }, {
                        default: _withCtx(() => [
                          _createVNode(ClipIcon)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(ElButton), {
                    round: "",
                    onClick: addGeneralFile
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('buttons.loadFile')) + " ", 1),
                      _createVNode(_unref(ElIcon), { class: "el-icon--right" }, {
                        default: _withCtx(() => [
                          _createVNode(UploadIcon)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(ElRow), null, {
            default: _withCtx(() => [
              _createVNode(_unref(ElCol), { class: "general-dialog__between" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ElButton), {
                    class: "ui-btn --big-round",
                    onClick: undoChanges
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('cancel')), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(ElButton), {
                    type: "primary",
                    class: "ui-btn --big-round",
                    icon: _unref(SuccessFilled),
                    onClick: checkValid
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('save')), 1)
                    ]),
                    _: 1
                  }, 8, ["icon"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["model", "rules"])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})